import { MAX_FREE_UPLOADS } from '@listening/api-contract';
import { useFreeUploadWeekStartMs } from '@listening/frontend/hooks/use-free-upload-week-start';
import type { PlanKey, PortalFlow } from '@listening/api-contract/schemas';
import {
  billingCancelEffect,
  billingChangePlanEffect,
  billingPreviewPlanChangeEffect,
  billingSubscribeEffect,
  billingPortalSessionEffect,
  billingStatusQueryKey,
  checkoutConfigQueryKeyPrefix,
  Feedback,
  paymentDetailsQueryKey,
  paymentSubscriptionsQueryKey,
  userSummaryQueryKey,
} from '@listening/frontend/query/user';
import { useBillingStatusQuery } from '@listening/frontend/query/user-hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { Effect } from 'effect';
import { useCallback, useEffect } from 'react';
import { useFreePlan } from '@/hooks/use-free-plan-availability';
import { useUploadsThisWeek } from '@listening/frontend/query/audio-list';
import { rootLogger } from '@/lib/root-logger';
import { eq } from '@/runtimes/effect-query';
import { useFlag } from '@listening/frontend/analytics/client';
import {
  entitledFor,
  notEntitledFor,
  paywallShownFor,
} from './subscription-gate';
import { useIsUserLoggedIn } from './user-service';
import { useAuthSession } from './auth-service';

const moduleLogger = rootLogger.child({
  module: 'SubscriptionService',
});

// Auth-gated metering derivations — app-specific (gate on the app session hook),
// reading the shared user-summary query. `usePaymentSubscriptionsQuery` /
// `usePaymentDetailsQuery` now live in `@listening/frontend/query/user-hooks`.

/**
 * False until /billing/status has settled (session resolving, query in flight,
 * or errored). Safe for restricting free users, since it never flags a user
 * as free-tier prematurely.
 */
export const useIsInvalidSubscription = () => {
  const userLoggedIn = useIsUserLoggedIn();
  return notEntitledFor(useBillingStatusQuery({ enabled: !!userLoggedIn }));
};

/**
 * Whether the paywall counts as shown — not subscribed as far as we can tell,
 * or a lookup that failed. `not-shown` while the session resolves or the query
 * is in flight.
 *
 * Deliberately separate from [useIsInvalidSubscription], which must stay
 * conservative: it gates access to paid features, so a blip on /billing/status
 * cannot be allowed to revoke them. This one only reports what the user is
 * looking at, where a failed lookup means the paywall is on screen and going
 * quiet would undercount it. See `premium_paywall_interaction.opened`.
 */
export const usePaywallShown = () => {
  const userLoggedIn = useIsUserLoggedIn();
  return paywallShownFor(useBillingStatusQuery({ enabled: !!userLoggedIn }));
};

/**
 * Also false until /billing/status has settled, so a premium user reads as free
 * during initial load. Reactive renders self-correct once data lands, but
 * point-in-time decisions (event handlers) must NOT gate on this — settle the
 * billing-status query first (see `onDrop` in file-upload-service.ts).
 */
export const useIsValidSubscription = () => {
  const userLoggedIn = useIsUserLoggedIn();
  return entitledFor(useBillingStatusQuery({ enabled: !!userLoggedIn }));
};

const useFreePlanRestrictions = () => {
  const { isInFreePlanCountry, isResolvingCountry } = useFreePlan();
  const isSubscriptionInvalid = useIsInvalidSubscription();

  const { value: ffBypassUploadLimit } = useFlag('bypassClientUploadPrecheck');
  const { value: ffBypassPrivateAudioLimit } = useFlag(
    'bypassFreePlanPrivateAudioLimit',
  );

  return {
    uploadLimitEnabled:
      isSubscriptionInvalid &&
      !isInFreePlanCountry &&
      !isResolvingCountry &&
      ffBypassUploadLimit !== 'true',
    privateAudioLimitEnabled:
      isSubscriptionInvalid && ffBypassPrivateAudioLimit !== 'true',
  };
};

export const useCancelStripeMutation = () => {
  // The legacy endpoint accepted `{cancelReason, likedFeatures}` as a
  // free-form body and silently dropped likedFeatures server-side. The
  // typed Effect contract only accepts `{reason?: string}`, so we now
  // route likedFeatures through the dedicated PostCancelSurvey endpoint
  // (which already exists for the delete-account flow). Fire-and-forget
  // — the cancel is what the user is waiting on; the survey is analytics
  // that should not block.
  const queryClient = useQueryClient();
  return useMutation({
    ...eq.mutationOptions({
      mutationFn: (params: { cancelReason: string; likedFeatures: string }) =>
        Effect.gen(function* () {
          // The survey is fire-and-forget analytics that must not block the
          // cancel: fork it as a daemon so it runs independently, and log any
          // failure through Effect logging (not the deprecated pino logger).
          yield* Effect.forkDetach(
            Feedback.submitCancellationSurvey({
              is_user_deleted: false,
              user_cancel_reason: params.cancelReason,
              user_liked_features: params.likedFeatures,
            }).pipe(
              Effect.catch((error) =>
                Effect.logError(
                  'PostCancelSurvey failed (non-blocking)',
                  error,
                ),
              ),
            ),
          );
          const result = yield* billingCancelEffect({
            reason: params.cancelReason,
          });
          yield* Effect.logInfo('Stripe subscription canceled');
          return result;
        }),
    }),
    onSuccess: () => {
      // Canceling flips `cancel_at_period_end` on the subscription and
      // `metering.is_active_subscription` on the summary; both caches have a
      // 60-min `staleTime`, so without an explicit invalidation the UI keeps
      // showing the active-subscription state for up to an hour. Mirrors the
      // pre-migration mutation's built-in invalidation.
      void queryClient.invalidateQueries({
        queryKey: paymentSubscriptionsQueryKey,
      });
      void queryClient.invalidateQueries({ queryKey: userSummaryQueryKey });
      void queryClient.invalidateQueries({ queryKey: billingStatusQueryKey });
      void queryClient.invalidateQueries({
        queryKey: checkoutConfigQueryKeyPrefix,
      });
    },
  });
};
export const planChangePreviewQueryKey = (planId: PlanKey) =>
  ['billing', 'change-plan', 'preview', planId] as const;

/**
 * What a plan change would cost, read once per confirmation.
 *
 * Uncached on purpose — `staleTime: 0`, `gcTime: 0`, no retry. The amount this
 * returns is the amount the confirm is held to, so a second reader must get a
 * second computation rather than the one shown minutes ago, and a preview left
 * in the cache is one the server would refuse. No retry because a failed
 * preview is a sentence the user reads and a button they press again, not a
 * background recovery.
 */
export const usePlanChangePreviewQuery = (params: {
  planId: PlanKey;
  enabled: boolean;
}) =>
  useQuery({
    ...eq.queryOptions({
      queryKey: planChangePreviewQueryKey(params.planId),
      queryFn: () => billingPreviewPlanChangeEffect({ plan_id: params.planId }),
      staleTime: 0,
    }),
    gcTime: 0,
    retry: false,
    enabled: params.enabled,
  });

/**
 * Move to another catalog plan.
 *
 * Takes a `PlanKey`, never a price id: the server resolves the price by lookup
 * key, so a price experiment or a repricing needs no client deploy, and naming
 * an uncatalogued price is unrepresentable rather than merely rejected.
 *
 * The proration instant and the amount come from the preview the person
 * confirmed: the server recomputes against them and refuses when the cost has
 * moved, so no request here can charge an amount that was never shown.
 */
export const useChangePlanMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    ...eq.mutationOptions({
      mutationFn: (params: {
        planId: PlanKey;
        prorationDate: string;
        expectedDueTodayCents: number;
        idempotencyKey: string;
      }) =>
        billingChangePlanEffect(
          {
            plan_id: params.planId,
            proration_date: params.prorationDate,
            expected_due_today_cents: params.expectedDueTodayCents,
          },
          params.idempotencyKey,
        ).pipe(
          Effect.tap(() => Effect.logInfo('Stripe subscription modified')),
        ),
    }),
    onSuccess: () => {
      // Changing tier (incl. an expired user re-subscribing via the plan
      // picker) flips the subscription + `metering.is_active_subscription` on
      // the summary. Both caches have a 60-min `staleTime`, so without this
      // invalidation the user keeps seeing the stale plan — and stays gated out
      // of paid features (see `useIsInvalidSubscription`) — for up to an hour.
      // Mirrors the cancel mutation above and the pre-migration behavior.
      void queryClient.invalidateQueries({
        queryKey: paymentSubscriptionsQueryKey,
      });
      void queryClient.invalidateQueries({ queryKey: userSummaryQueryKey });
      void queryClient.invalidateQueries({ queryKey: billingStatusQueryKey });
      void queryClient.invalidateQueries({
        queryKey: checkoutConfigQueryKeyPrefix,
      });
    },
  });
};
/**
 * Clear a pending cancellation — the uncancel arm of `POST /billing/subscribe`.
 *
 * Posts the plan the user is already on, because the endpoint's uncancel branch
 * matches on the live subscription, not on the request. A response carrying a
 * `client_secret` means a card is needed after all — an owed-time resubscribe's
 * SetupIntent, or a fresh checkout — and the caller routes it by
 * `confirmation_type`.
 */
export const useUncancelMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    ...eq.mutationOptions({
      mutationFn: (params: { planId: PlanKey; uncancelOnly: boolean }) =>
        billingSubscribeEffect({
          plan_id: params.planId,
          ...(params.uncancelOnly ? { uncancel_only: true } : {}),
        }),
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingStatusQueryKey });
      void queryClient.invalidateQueries({
        queryKey: checkoutConfigQueryKeyPrefix,
      });
      void queryClient.invalidateQueries({ queryKey: userSummaryQueryKey });
      void queryClient.invalidateQueries({
        queryKey: paymentSubscriptionsQueryKey,
      });
    },
  });
};

/**
 * Mint a billing-portal session and hand the browser to Stripe.
 *
 * Stripe's portal collects the card and carries invoice history, which replaces
 * the bespoke card form this app used to embed. Minted on click because a portal
 * session expires five minutes after it is created.
 */
export const usePortalSessionMutation = () =>
  useMutation({
    ...eq.mutationOptions({
      mutationFn: (params: { flow: PortalFlow }) =>
        billingPortalSessionEffect({ flow: params.flow }),
    }),
    onSuccess: (session) => {
      window.location.href = session.url;
    },
    onError: (error) => {
      moduleLogger.error(error);
    },
  });

/**
 * Drop every read taken before a purchase settled — billing status, the user
 * summary, and the subscription/payment lists.
 *
 * Awaiting the result waits for the refetches, which is what a checkout must do
 * before it navigates: the page it leaves for was primed while the buyer was
 * still unentitled, and `/billing/status` is cached for an hour, so without this
 * they land on a paid page that still reads them as free tier — or get bounced
 * back to the paywall by the dashboard's entitlement guard.
 */
export const useSettleBillingCaches = () => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: billingStatusQueryKey }),
      queryClient.invalidateQueries({
        queryKey: checkoutConfigQueryKeyPrefix,
      }),
      queryClient.invalidateQueries({ queryKey: userSummaryQueryKey }),
      queryClient.invalidateQueries({
        queryKey: paymentSubscriptionsQueryKey,
      }),
      queryClient.invalidateQueries({ queryKey: paymentDetailsQueryKey }),
    ]);
  }, [queryClient]);
};

/**
 * Cleans up after a Stripe redirect return, on `/post-purchase`.
 *
 * The **write** is the route loader's, not this hook's: it calls
 * `POST /billing/confirm` during SSR, which syncs from Stripe, stores a
 * SetupIntent's card as the default and marks the user as having paid. By the
 * time this runs the truth is already settled server-side, so all that is left
 * is to strip the intent ids out of the URL and drop the caches that were read
 * before the purchase.
 *
 * The "Complete setup" dialog that used to finish an `incomplete` subscription
 * from the plan page is gone — the paywall is the resume path — and it was the
 * only flow that landed a `payment_intent` anywhere but here.
 */
export const useCheckoutReturnCleanup = () => {
  const settleBillingCaches = useSettleBillingCaches();
  const search: {
    payment_intent?: string;
    setup_intent?: string;
  } = useSearch({ strict: false });

  useEffect(() => {
    if (!search.payment_intent && !search.setup_intent) return;

    moduleLogger.info(
      {
        payment_intent: search.payment_intent,
        setup_intent: search.setup_intent,
      },
      'Stripe redirect post-purchase cleanup',
    );

    window.history.replaceState({}, '', window.location.pathname);

    void settleBillingCaches();
  }, [settleBillingCaches, search.payment_intent, search.setup_intent]);
};

export const useFreePlanUploadLimit = () => {
  // One bounded page of non-errored audios (server except_status=Error,
  // sort=created_at). The week-start filter below counts this week's uploads;
  // a free user with >1 page of uploads in a week is already over the limit.
  const audioAndQueue = useUploadsThisWeek().data ?? [];
  const { user } = useAuthSession();
  const lastResetMs = useFreeUploadWeekStartMs();

  const uploadsSinceReset = audioAndQueue.filter(
    (audio) =>
      audio.owner_id === user?.userId && audio.created_at_ms >= lastResetMs,
  );
  const maxUploads = MAX_FREE_UPLOADS;
  const remainingUploads = Math.max(maxUploads - uploadsSinceReset.length, 0);
  const { uploadLimitEnabled } = useFreePlanRestrictions();

  return {
    remainingUploads,
    maxUploads,
    overUploadLimit: remainingUploads <= 0 && uploadLimitEnabled,
    limitEnabled: uploadLimitEnabled,
  };
};

export const useFreePlanPrivateAudioLimit = () => {
  const { privateAudioLimitEnabled } = useFreePlanRestrictions();
  return {
    limitEnabled: privateAudioLimitEnabled,
  };
};
